import { useState } from 'react';
import styled from 'styled-components';
import { createNameList, formatEpochMsToDisplayDate, getFullName } from './sortingHelper';
import { createDragElement } from './drag-and-drop';
import ProductCover from '../../../../shared/common/ProductCover';
import { useTranslation } from 'react-i18next';
import { FONT_BASE, themeFontSize } from '../../../../shared/common/fontSizes';
import { THEME_ALU, themeColor } from '../../../../styles/basics/colors';
import { ProductGroupIcon } from '../../../../shared/common/ProductGroupDropdown/ProductGroupIcon';
import { LocalContentCheckBox } from '../../../localcontent/product/publishing/LocalContentCheckBox';
import { ProductOut } from '../../../../store/api';

interface ProductLineProps {
    isInSelectionMode: boolean;
    lineIsSelected: boolean;
    lineIsDragged: boolean;
}

const StProductLine = styled.div<ProductLineProps>`
    height: 65px;
    display: flex;
    padding-top: 17px;
    padding-bottom: 5px;
    padding-right: ${props => (!props.isInSelectionMode ? '70px' : 'unset')};
    margin-right: ${props => (props.isInSelectionMode ? '120px' : '10px')};
    background-color: ${props => (props.lineIsSelected || props.lineIsDragged ? '#ecf6de' : null)};
    cursor: pointer;
`;

const StMetaDataParagraph = styled.p`
    font-size: ${themeFontSize(FONT_BASE)};
    line-height: 16px;
    margin-bottom: 10px;
`;

const StHeadLineListItem = styled(StMetaDataParagraph)`
    font-weight: 700;
`;

const StCheckboxRow = styled.div`
    width: 65px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StPackshotRow = styled.div`
    width: 75px;

    img {
        width: 35px;
    }

    display: flex;
    align-items: center;
`;

const StMetaDataRow = styled.div`
    width: 230px;
    padding-right: 20px;
`;

const StDragElement = styled.div`
    margin-left: auto;
    align-self: center;
    margin-top: -8px;
`;

const StIcon = styled(ProductGroupIcon)`
    font-size: 24px;
    color: ${themeColor(THEME_ALU)};
`;

interface Props {
    product: ProductOut;
    currentRank: string;
    handleProductSelection: () => void;
    isInSelectionMode: boolean;
    isSelected: boolean;
}

export const ProductItem = ({ product, handleProductSelection, isSelected, isInSelectionMode, currentRank }: Props) => {
    const { t } = useTranslation();
    const [isDragged, setDragged] = useState(false);

    const authors = product.authors && product.authors.length > 0 ? createNameList(product.authors) : '';
    const narrators = product.narrators && product.narrators.length > 0 ? createNameList(product.narrators) : '';
    const releaseDate = product.releaseDate ? formatEpochMsToDisplayDate(product.releaseDate, t) : '';

    return (
        <StProductLine
            isInSelectionMode={isInSelectionMode}
            lineIsSelected={isSelected}
            lineIsDragged={isDragged}
            onClick={handleProductSelection}
            data-displayname="FixedProductGroup-Sorting-ProductItem"
        >
            <StCheckboxRow>
                <LocalContentCheckBox isChecked={isSelected} />
            </StCheckboxRow>
            <StPackshotRow>
                <ProductCover product={product} minSizeInPx={35} />
            </StPackshotRow>
            <StMetaDataRow>
                {product.seriesName ? (
                    <StMetaDataParagraph>
                        {product.seriesName} {product.seriesNumber}
                    </StMetaDataParagraph>
                ) : null}
                <StHeadLineListItem>
                    <span data-displayname="FixedProductGroup-Sorting-ProductItem-Title">{product.title}</span>
                </StHeadLineListItem>
            </StMetaDataRow>
            <StMetaDataRow>
                <StMetaDataParagraph>{authors}</StMetaDataParagraph>
                <StMetaDataParagraph>{narrators}</StMetaDataParagraph>
            </StMetaDataRow>
            <StMetaDataRow>
                <StMetaDataParagraph>{releaseDate}</StMetaDataParagraph>
                <StMetaDataParagraph>
                    {product.publisher} - {product.publisherImprint}
                </StMetaDataParagraph>
            </StMetaDataRow>
            {!isInSelectionMode ? (
                <StDragElement
                    draggable={true}
                    onDragStart={e => {
                        setDragged(true);

                        const authors = product?.authors ?? [];
                        const { firstName = '', lastName = '' } = authors.length > 0 ? authors[0] : {};
                        const authorFullName = firstName || lastName ? ` - ${getFullName(firstName, lastName)}` : '';
                        const elem = createDragElement(
                            `${product.titleWithoutPrefix}${authorFullName}`,
                            'dragged-product'
                        );
                        document.body.appendChild(elem);
                        e.dataTransfer.setDragImage(elem, 20, 20);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text', currentRank);
                    }}
                    onDragEnd={() => {
                        [...document.getElementsByClassName('dragged-product')].forEach(n => n && n.remove());
                        setDragged(false);
                    }}
                >
                    <StIcon icon={'menu'} />
                </StDragElement>
            ) : null}
        </StProductLine>
    );
};
